import { useCallback, useEffect, useSyncExternalStore } from 'react';
import config from '../config';
import * as vfsClient from '../utils/vfsClient';
import { connectProjectTreeSyncRaw } from '../utils/vfsSync';
import { isComposeService } from '../utils/serviceStackModel';

/**
 * The project's services as folders: `services/<name>` (utils/projectLayout.js: the brain is never one), read from
 * vfs-service (two levels, so each folder's own file names say which one is a Docker Compose app). One answer per
 * project for the whole page, like useProjectServices: Home's Apps, Needs attention, the header's Start all and the
 * Apps tab read the same store, so they never disagree about which services there are.
 *
 * Read again (by every caller at once) on `notifyServicesChanged(projectId)`: a service added (Apps' "+", Home's "+",
 * Start by…'s import), deleted, renamed or moved; on the project's tree events (one socket per project while anyone
 * reads the store: the first file of a new service folder, a service folder deleted or moved, a terminal command);
 * on the Home's Refresh and live refresh; and when the runner's count of services changes (`refreshKey`). When the
 * list changed, `flow:servicesChanged` { projectId } tells the rest of the page (Canvas reloads the configuration the
 * header's service picker and Preview read).
 *
 * Returns `{ folders, composed, loaded, refresh }`: `folders` null while unknown (not read yet, or never readable: an
 * unknown list is not an empty one), else the sorted `services/<name>` paths ([] when there is no `services/`).
 */

export const SERVICES_CHANGED_EVENT = 'flow:servicesChanged';

const stores = new Map(); // projectId -> { snapshot: { folders, composed, loaded }, listeners: Set, inflight }
const EMPTY = { folders: null, composed: [], loaded: false };

function storeFor(projectId) {
  let store = stores.get(projectId);
  if (!store) {
    store = { snapshot: EMPTY, listeners: new Set(), inflight: null, again: false, disconnect: null, timer: null };
    stores.set(projectId, store);
  }
  return store;
}

const sameList = (a, b) => Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((item, i) => item === b[i]);

/** Reads the project's service folders again; a read asked while one is in flight runs once more after it. */
export async function refreshServiceFolders(projectId) {
  if (!projectId) { return null; }
  const store = storeFor(projectId);
  if (store.inflight) { store.again = true; return store.inflight; }
  store.inflight = (async() => {
    try {
      let tree = null;
      try {
        tree = await vfsClient.readTree(projectId, 'services', 2); // null: no services/ yet, no services
      } catch {
        // Not readable now (a blip): the last answer stays; never read, the list stays unknown (null).
        if (!store.snapshot.loaded) {
          store.snapshot = { ...EMPTY, loaded: true };
          store.listeners.forEach((listener) => listener());
        }
        return store.snapshot;
      }
      const dirs = (tree?.children || []).filter((child) => child.type === 'directory' && !child.name.startsWith('.'))
        .sort((a, b) => a.name.localeCompare(b.name));
      const folders = dirs.map((child) => `services/${child.name}`);
      const composed = dirs.filter((child) => isComposeService({ names: (child.children || []).map((item) => item.name) })).map((child) => `services/${child.name}`);
      const before = store.snapshot;
      if (!before.loaded || !sameList(before.folders, folders) || !sameList(before.composed, composed)) {
        store.snapshot = { folders, composed, loaded: true };
        store.listeners.forEach((listener) => listener());
        // The list itself changed after a first answer: the rest of the page follows.
        if (Array.isArray(before.folders) && !sameList(before.folders, folders)) { announce(projectId); }
      }
      return store.snapshot;
    } finally {
      store.inflight = null;
      if (store.again) { store.again = false; refreshServiceFolders(projectId); }
    }
  })();
  return store.inflight;
}

function announce(projectId) {
  try { window.dispatchEvent(new CustomEvent(SERVICES_CHANGED_EVENT, { detail: { projectId } })); } catch { /* no window */ }
}

/**
 * Something changed the project's services (added, deleted, renamed, moved): every reader of the store reads them
 * again, and the rest of the page (Canvas's configuration) hears `flow:servicesChanged` even when the folder list
 * looks the same (a service's contents replaced).
 */
export async function notifyServicesChanged(projectId) {
  if (!projectId) { return; }
  const before = storeFor(projectId).snapshot.folders;
  await refreshServiceFolders(projectId);
  if (sameList(before, storeFor(projectId).snapshot.folders)) { announce(projectId); }
}

/** Whether a project path is a service folder itself (`services/<name>`), the paths whose create/delete/move changes the list. */
export const isServiceFolderPath = (path) => /^\/?services\/[^/]+\/?$/.test(String(path || ''));

/** The service a project path is in (`services/web/src/a.js` → `services/web`), or null. */
const serviceOfPath = (path) => {
  const match = /^\/?services\/([^/]+)/.exec(String(path || ''));
  return match ? `services/${match[1]}` : null;
};

/**
 * Whether a tree event may change the list: a path in a service folder the list does not have yet (its first file),
 * or a service folder itself deleted or moved.
 */
export function changesServiceList(folders, { path = null, from = null, to = null, deleted = false } = {}) {
  const known = (candidate) => Array.isArray(folders) && folders.includes(serviceOfPath(candidate));
  if (path && serviceOfPath(path)) { return deleted ? isServiceFolderPath(path) || !known(path) : !known(path); }
  return [from, to].some((candidate) => candidate && (isServiceFolderPath(candidate) || (serviceOfPath(candidate) && !known(candidate))));
}

/** The project's tree events (one socket per project while someone reads the store): read again when they say so. */
function watchTree(projectId, store) {
  if (store.disconnect || !config.vfsServiceUrl || typeof WebSocket === 'undefined') { return; }
  const soon = () => { clearTimeout(store.timer); store.timer = setTimeout(() => refreshServiceFolders(projectId), 400); };
  const check = (event) => { if (changesServiceList(store.snapshot.folders, event)) { soon(); } };
  store.disconnect = connectProjectTreeSyncRaw(config.vfsServiceUrl, projectId, {
    onFullResync: (type) => { if (type !== 'tree:init') { soon(); } },
    onPathChanged: (path) => check({ path }),
    onPathDeleted: (path) => check({ path, deleted: true }),
    onPathMoved: (from, to) => check({ from, to }),
  });
}

function unwatchTree(store) {
  clearTimeout(store.timer);
  if (store.disconnect) { store.disconnect(); store.disconnect = null; }
}

/** Test helper: forgets every project's answer. */
export function resetServiceFoldersForTests() { stores.forEach(unwatchTree); stores.clear(); }

const noop = () => () => {};

export default function useServiceFolders(projectId, { refreshKey = null } = {}) {
  const subscribe = useCallback((listener) => {
    const store = storeFor(projectId);
    store.listeners.add(listener);
    watchTree(projectId, store);
    return () => {
      store.listeners.delete(listener);
      if (store.listeners.size === 0) { unwatchTree(store); }
    };
  }, [projectId]);
  const snapshot = useSyncExternalStore(projectId ? subscribe : noop, () => (projectId ? storeFor(projectId).snapshot : EMPTY));
  // First reader of a project (and on `refreshKey`, a caller's own signal such as the runner's service count): read.
  useEffect(() => { if (projectId) { refreshServiceFolders(projectId); } }, [projectId, refreshKey]);
  return { ...snapshot, refresh: () => refreshServiceFolders(projectId) };
}
